if (!customElements.get('cart-recommendations')) {
  // Server returns up to 10 products per intent; the snippet drops cart items.
  const FETCH_LIMIT = 10;
  const INTENTS = ['complementary', 'related'];
  // Matches the drawer slide (--duration-default) plus a small margin.
  const CLOSE_DURATION = 300;
  const LOADING_DELAY = 150;
  const PREFETCH_TIMEOUT = 2000;
  // Products tried as the recommendation seed: the last added one, then up to this many total from the cart.
  const MAX_SEEDS = 4;
  const CACHE_TTL = 5 * 60 * 1000;
  const CACHE_SIZE = 10;
  // Cards already fetched, keyed by context + cart contents. Survives drawer re-renders, which rebuild the element.
  const cache = new Map();
  // Cards last shown per context, kept on screen (dimmed) while a re-rendered element fetches fresh ones.
  const lastShown = {};
  // Keep in sync with the panel / band breakpoint in component-cart-recommendations.css.
  const BREAKPOINT = '(min-width: 1068px)';

  class CartRecommendations extends HTMLElement {
    constructor() {
      super();
      this.requestId = 0;
    }

    connectedCallback() {
      if (this.dataset.context === 'page') {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.load());
        this.load();
        return;
      }

      // Drawer band and slide-out panel: the drawer re-renders on every cart change, so load
      // only while it is open and follow its open/close state.
      this.drawer = this.closest('cart-drawer');
      if (!this.drawer) {
        this.load();
        return;
      }
      this.drawerObserver = new MutationObserver(() => this.syncWithDrawer());
      this.drawerObserver.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });

      // The panel sits beside .drawer__inner, which is the only part re-rendered on remove / quantity
      // changes, so it must refresh itself. The band lives inside .drawer__inner and is replaced instead.
      if (this.isPanel) {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
          if (this.drawer.classList.contains('active')) this.load();
        });
      }

      // Crossing the breakpoint while the drawer is open (window resize, device toggle) switches which
      // copy is displayed, and the newly displayed one has not loaded yet.
      this.breakpoint = window.matchMedia(BREAKPOINT);
      this.onBreakpointChange = () => {
        if (this.drawer.classList.contains('active')) this.load();
      };
      this.breakpoint.addEventListener('change', this.onBreakpointChange);

      // Re-rendered while the drawer is already open: skip the slide-out animation.
      const drawerIsOpen = this.drawer.classList.contains('active');
      this.skipAnimation = drawerIsOpen;

      // Cards for this exact cart are shown straight away. Cards from the previous cart are only kept on screen
      // while the drawer is already open; with the drawer closed they would slide out and then visibly change
      // when the new ones arrive (adding a product re-renders the drawer and opens it right after).
      this.restoreFromCache({ allowStale: drawerIsOpen });
      this.syncWithDrawer();

      if (lastShown[this.dataset.context]) {
        // Not the first render on this page: the cart changed, fetch the new cards now so they are ready as soon
        // as the drawer opens.
        this.load({ useServerState: true });
      } else {
        // First render: prefetch while idle so the cards are there when the drawer opens (slow connections).
        this.schedulePrefetch();
      }
    }

    schedulePrefetch() {
      const prefetch = () => {
        if (this.isConnected && !this.hasLoaded) this.load({ useServerState: true });
      };
      if ('requestIdleCallback' in window) requestIdleCallback(prefetch, { timeout: PREFETCH_TIMEOUT });
      else setTimeout(prefetch, PREFETCH_TIMEOUT);
    }

    disconnectedCallback() {
      clearTimeout(this.closingTimer);
      clearTimeout(this.loadingTimer);
      this.breakpoint?.removeEventListener('change', this.onBreakpointChange);
      this.cartUpdateUnsubscriber?.();
      this.drawerObserver?.disconnect();
      this.requestId++;
    }

    get limit() {
      return parseInt(this.dataset.limit, 10) || 4;
    }

    get isPanel() {
      return this.dataset.context === 'panel';
    }

    // The panel shows on wide screens, the in-drawer band on narrow ones (see the CSS breakpoint).
    get isDisplayed() {
      const isWide = window.matchMedia(BREAKPOINT).matches;
      return this.isPanel ? isWide : !isWide;
    }

    syncWithDrawer() {
      if (this.drawer.classList.contains('active')) {
        this.endClosing();
        if (this.hasLoaded) this.reveal();
        this.load({ useServerState: true });
      } else if (this.classList.contains('active')) {
        this.startClosing();
      }
    }

    // On close the panel slides back with the drawer instead of lingering after it is gone.
    startClosing() {
      this.classList.remove('active');
      this.classList.add('is-closing');
      this.closingTimer = setTimeout(() => this.endClosing(), CLOSE_DURATION);
    }

    endClosing() {
      clearTimeout(this.closingTimer);
      this.classList.remove('is-closing');
    }

    reveal() {
      if (!this.isPanel || this.hidden) return;
      if (this.skipAnimation) {
        this.classList.add('active');
        return;
      }
      requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('active')));
    }

    // Spinner (and dimmed list) only after a short delay, so fast responses don't flash.
    startLoading() {
      clearTimeout(this.loadingTimer);
      this.setAttribute('aria-busy', 'true');
      this.loadingTimer = setTimeout(() => this.classList.add('is-loading'), LOADING_DELAY);
    }

    endLoading() {
      clearTimeout(this.loadingTimer);
      this.removeAttribute('aria-busy');
      this.classList.remove('is-loading');
    }

    // Reuse cards from before the drawer re-rendered, so the panel does not vanish and wait for the network.
    restoreFromCache({ allowStale = false } = {}) {
      const cached = this.readCache(this.dataset.signature);
      if (cached) {
        this.loadedSignature = this.dataset.signature;
        this.render(this.parseItems(cached));
        return;
      }
      const previous = lastShown[this.dataset.context];
      if (allowStale && previous) this.render(this.parseItems(previous));
    }

    cacheKey(signature) {
      return `${this.dataset.context}|${signature}`;
    }

    readCache(signature) {
      const entry = cache.get(this.cacheKey(signature));
      if (!entry) return null;
      if (Date.now() - entry.time > CACHE_TTL) {
        cache.delete(this.cacheKey(signature));
        return null;
      }
      return entry.html;
    }

    writeCache(signature, html) {
      cache.set(this.cacheKey(signature), { html, time: Date.now() });
      if (cache.size > CACHE_SIZE) cache.delete(cache.keys().next().value);
    }

    parseItems(html) {
      const template = document.createElement('template');
      template.innerHTML = html.join('');
      return [...template.content.children];
    }

    // useServerState: take the cart contents the server rendered into this element instead of asking /cart.js.
    // Right after a drawer render that is current, and it saves a round trip before the cards can load.
    async load({ useServerState = false } = {}) {
      if (this.drawer && !this.isDisplayed) return;
      // Already fetching exactly this cart (e.g. started on connect, and the drawer opening asks again).
      if (useServerState && this.inFlightSignature && this.inFlightSignature === this.dataset.signature) return;
      const requestId = ++this.requestId;

      try {
        let signature = this.dataset.signature;
        let seedProductId = this.dataset.seed;

        if (!useServerState || !seedProductId) {
          const cart = await fetch(`${routes.cart_url}.js`).then((response) => response.json());
          if (requestId !== this.requestId) return;

          if (!cart.items.length) {
            this.loadedSignature = null;
            this.render([]);
            return;
          }

          // Seed with the most recently added line (first item in cart.items).
          signature = cart.items.map((item) => item.product_id).join(',');
          seedProductId = String(cart.items[0].product_id);
          // Keep the server-rendered state current: the panel is not re-rendered on remove / quantity changes.
          this.dataset.signature = signature;
          this.dataset.seed = seedProductId;
        }

        // Nothing relevant changed since the cards on screen were loaded (e.g. the drawer just re-opened).
        if (this.hasLoaded && signature === this.loadedSignature) return;

        const cached = this.readCache(signature);
        if (cached) {
          this.loadedSignature = signature;
          this.render(this.parseItems(cached));
          return;
        }

        this.inFlightSignature = signature;

        // Only dim the existing cards and show the spinner when there are cards to replace. On the very first
        // load the element stays hidden until it has content, so there is no spinner on opening the drawer.
        if (this.hasLoaded) this.startLoading();

        // Seed with the last added product. If Shopify has no recommendations for it (e.g. a very new product),
        // fall through to the other products in the cart so the area is not left empty.
        const seeds = [...new Set([seedProductId, ...signature.split(',')])].slice(0, MAX_SEEDS);
        const items = new Map();

        for (const seed of seeds) {
          // Both intents in parallel (one round trip, not two); complementary results come first.
          const results = await Promise.all(INTENTS.map((intent) => this.fetchItems(seed, intent)));
          if (requestId !== this.requestId) return;

          results.flat().forEach((item) => {
            if (items.size < this.limit && !items.has(item.dataset.productId)) {
              items.set(item.dataset.productId, item);
            }
          });
          if (items.size) break;
        }

        // Never cache or remember an empty result: Shopify can return nothing for a product that has not been
        // processed yet, and that must not hide the area for the next few minutes.
        if (items.size) {
          this.loadedSignature = signature;
          this.writeCache(
            signature,
            [...items.values()].map((item) => item.outerHTML),
          );
        } else {
          this.loadedSignature = null;
        }
        this.render([...items.values()]);
      } catch (error) {
        console.error('Cart recommendations failed to load', error);
      } finally {
        // A newer load() owns the loading state until it finishes.
        if (requestId === this.requestId) {
          this.inFlightSignature = null;
          this.endLoading();
        }
      }
    }

    async fetchItems(productId, intent) {
      const params = new URLSearchParams({
        product_id: productId,
        limit: FETCH_LIMIT,
        intent,
        section_id: this.dataset.sectionId,
      });
      const text = await fetch(`${window.Shopify.routes.root}recommendations/products?${params}`).then((response) =>
        response.text(),
      );
      const doc = new DOMParser().parseFromString(text, 'text/html');
      return [
        ...doc.querySelectorAll(
          `cart-recommendations[data-context="${this.dataset.context}"] .cart-recommendations__item`,
        ),
      ];
    }

    render(items) {
      const list = this.querySelector('.cart-recommendations__list');
      if (!list) return;

      this.querySelector('.cart-recommendations__heading')?.remove();
      list.replaceChildren(...items.map((item) => document.importNode(item, true)));
      this.hidden = items.length === 0;
      this.hasLoaded = true;
      if (items.length) lastShown[this.dataset.context] = items.map((item) => item.outerHTML);

      if (items.length) {
        // Cart page: an h2 under the page h1. Mini cart: a span, like the drawer's own title (the block is a
        // labelled region instead, so there is no lone heading in a drawer that has none).
        const heading = document.createElement(this.dataset.headingTag || 'h2');
        heading.className = `cart-recommendations__heading ${this.dataset.headingClass || 'h5'}`;
        heading.textContent = this.dataset.heading;
        list.before(heading);
        if (this.drawer?.classList.contains('active')) this.reveal();
      } else {
        this.classList.remove('active');
      }
    }
  }

  customElements.define('cart-recommendations', CartRecommendations);
}
